import * as React from 'react';

import type { ApolloError, ApolloQueryResult } from 'apollo-client';

import type { LearnerProfileByExternalIdQuery } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileByExternalId';
import type { LearnerProfileMeQuery } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileMe';
import type { LearnerProfileMeFragment as LearnerProfileMe } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileMe';
import type { SharedLearnerProfile_ExternalProfileFragment as LearnerProfileExternal } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_externalProfile';
import {
  useLearnerProfileExternalIdQuery,
  useLearnerProfileMeQuery,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useLearnerProfileQuery';
import { useProfileBySlugWithRedirect } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';

import type { LearnerProfileByProfileSlugQuery } from '../__generated__/LearnerProfileByProfileSlug';

type SharedOutputProps = {
  isProfileLoading: boolean;
  refetchLearnerProfileMe?: () => Promise<ApolloQueryResult<LearnerProfileMeQuery>> | Promise<undefined>;
  refetchLearnerProfileExternalId?: () =>
    | Promise<ApolloQueryResult<LearnerProfileByExternalIdQuery>>
    | Promise<undefined>;
  refetchLearnerProfileProfileSlug?: () =>
    | Promise<ApolloQueryResult<LearnerProfileByProfileSlugQuery>>
    | Promise<undefined>;
  error?: ApolloError;
};

type OutputProps = SharedOutputProps & {
  learnerProfile?: LearnerProfileMe | LearnerProfileExternal | null;
};

type ChildrenInputProps = {
  children: (props: OutputProps) => React.ReactElement<OutputProps>;
};

type LearnerProfileExternalIdInputProps = ChildrenInputProps & {
  externalId: string;
};

type LearnerProfileProfileSlugInputProps = ChildrenInputProps & {
  profileSlug: string;
  redirect?: boolean;
};

type LearnerProfileMeInputProps = ChildrenInputProps;

type LearnerProfileQueryProviderInputProps = ChildrenInputProps & {
  externalId?: string;
  profileSlug?: string;
};

export const LearnerProfileByExternalIdProvider = ({ externalId, children }: LearnerProfileExternalIdInputProps) => {
  const { learnerProfile, isProfileLoading, refetchLearnerProfile, error } =
    useLearnerProfileExternalIdQuery(externalId);

  return children({
    learnerProfile,
    isProfileLoading,
    refetchLearnerProfileExternalId: refetchLearnerProfile,
    error,
  });
};

export const LearnerProfileByProfileSlugProvider = ({
  profileSlug,
  redirect,
  children,
}: LearnerProfileProfileSlugInputProps) => {
  const { learnerProfile, isProfileLoading, refetchLearnerProfile, error } = useProfileBySlugWithRedirect(
    profileSlug,
    redirect
  );

  return children({
    learnerProfile,
    isProfileLoading,
    refetchLearnerProfileProfileSlug: refetchLearnerProfile,
    error,
  });
};

const LearnerProfileMeProvider = ({ children }: LearnerProfileMeInputProps) => {
  const { learnerProfile, refetchLearnerProfile, isProfileLoading, error } = useLearnerProfileMeQuery();

  return children({
    learnerProfile,
    isProfileLoading,
    refetchLearnerProfileMe: refetchLearnerProfile,
    error,
  });
};

// We need to maintain this provider instead of completely replacing it with hooks because we only want to fetch either the LearnerProfileByExternalIdQuery
// or the LearnerProfileMeQuery, not both. You cannot call hooks conditionally, so we use each hook inside the respective provider.
export const LearnerProfileQueryProvider = ({
  externalId,
  profileSlug,
  children,
}: LearnerProfileQueryProviderInputProps) => {
  if (externalId) {
    return (
      <LearnerProfileByExternalIdProvider externalId={externalId}>
        {({ learnerProfile, refetchLearnerProfileExternalId, isProfileLoading, error }) => {
          return children({
            learnerProfile,
            isProfileLoading,
            refetchLearnerProfileExternalId,
            error,
          });
        }}
      </LearnerProfileByExternalIdProvider>
    );
  }

  if (profileSlug) {
    return (
      <LearnerProfileByProfileSlugProvider profileSlug={profileSlug} redirect={true}>
        {({ learnerProfile, refetchLearnerProfileProfileSlug, isProfileLoading, error }) => {
          return children({
            learnerProfile,
            isProfileLoading,
            refetchLearnerProfileProfileSlug,
            error,
          });
        }}
      </LearnerProfileByProfileSlugProvider>
    );
  }

  return (
    <LearnerProfileMeProvider>
      {({ learnerProfile, refetchLearnerProfileMe, isProfileLoading, error }) => {
        return children({
          learnerProfile,
          isProfileLoading,
          refetchLearnerProfileMe,
          error,
        });
      }}
    </LearnerProfileMeProvider>
  );
};
